import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Building2, ShieldCheck, Power, ArrowRight, Plus, Loader2 } from 'lucide-react';
import { fmtDate } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { WorkspaceDrawer } from '@/features/master/WorkspaceDrawer';

interface Workspace {
  id: string;
  slug: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  createdAt: string;
  adminEmail: string | null;
  userCount: number;
}

export const Route = createFileRoute('/_app/master/workspaces')({
  component: WorkspacesList,
});

function WorkspacesList() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['master', 'workspaces'],
    queryFn: () => api.get<{ items: Workspace[] }>('/v1/master/workspaces'),
  });

  async function impersonate(workspaceId: string, name: string) {
    try {
      await api.post(`/v1/master/workspaces/${workspaceId}/impersonate`);
      await refresh();
      toast.success(`Impersonando ${name}.`);
      window.location.href = '/';
    } catch {
      toast.error('Falha ao impersonar.');
    }
  }

  async function suspend(workspaceId: string, name: string, isActive: boolean) {
    try {
      await api.post(`/v1/master/workspaces/${workspaceId}/${isActive ? 'suspend' : 'reactivate'}`);
      await qc.invalidateQueries({ queryKey: ['master', 'workspaces'] });
      toast.success(isActive ? `${name} suspenso.` : `${name} reativado.`);
    } catch {
      toast.error('Falha na operação.');
    }
  }

  const workspaces = data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Master · Tenants</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Workspaces
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            Crie e gerencie tenants isolados. Cada workspace é uma entidade do terceiro setor.
          </p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Novo workspace
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-card-soft rounded-3 animate-pulse" />
          ))}
        </div>
      ) : workspaces.length === 0 ? (
        <Card variant="default" className="p-10 text-center">
          <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-mint-soft text-mint-deep shadow-[inset_0_2px_4px_rgba(40,90,70,0.10)]">
              <Building2 className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg">
                Nenhum workspace ainda
              </h3>
              <p className="text-sm text-fg-muted mt-1">
                Cadastre o primeiro tenant pra começar a operar. Você vira admin dele
                automaticamente via impersonação.
              </p>
            </div>
            <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.8} />
              Criar primeiro workspace
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {workspaces.map((w) => (
            <Card
              key={w.id}
              variant="default"
              className="p-4 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className="grid h-12 w-12 place-items-center rounded-2 bg-card-soft text-fg-muted shrink-0 border border-card-stroke shadow-pressed">
                  <Building2 className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-fg truncate">{w.name}</h3>
                    <span className="font-mono text-xs text-fg-dim">/{w.slug}</span>
                  </div>
                  <p className="text-xs text-fg-muted mt-0.5">
                    {w.userCount} usuário{w.userCount === 1 ? '' : 's'} · criado em{' '}
                    {fmtDate(w.createdAt)}
                    {w.adminEmail && <> · <span className="font-mono">{w.adminEmail}</span></>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Pill
                  tone={w.status === 'ACTIVE' ? 'pass' : w.status === 'SUSPENDED' ? 'warn' : 'block'}
                  dot
                >
                  {w.status === 'ACTIVE'
                    ? 'ativo'
                    : w.status === 'SUSPENDED'
                      ? 'suspenso'
                      : 'arquivado'}
                </Pill>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => suspend(w.id, w.name, w.status === 'ACTIVE')}
                  title={w.status === 'ACTIVE' ? 'Suspender' : 'Reativar'}
                >
                  <Power className="h-3.5 w-3.5" strokeWidth={1.8} />
                </Button>
                <Button size="sm" variant="default" onClick={() => impersonate(w.id, w.name)}>
                  <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.8} />
                  Impersonar
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <WorkspaceDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
